import React, { useCallback, useEffect, useState } from "react";

import { CloseOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";

import ExportCOAAllFilter from "./ExportCOAAllFilter";
import { initializeCheckedStates } from "./ExportCOAAllFilterHelper";
import ExportCOADataPreview from "./ExportCOADataPreview";
import ExportCOAModalHeader from "./ExportCOAModalHeader";
import { convertDateRange, handleCurrentMonthDate } from "./Utils";

import { exportCOAData } from "../../../../../actions/envReports";

import "./ExportCOAModal.css";

export default function ExportCOAModal({ handleCloseModal }) {
  const [showAllFilterDrawer, setShowAllFilterDrawer] = useState(false);
  const [dateSelected, setDateSelected] = useState("");
  const [envCOAData, setEnvCOAData] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [filteredSamples, setFilteredSamples] = useState([]);
  const [coaDates, setCoaDates] = useState([]);
  const [controller, setController] = useState(null);
  const [checkedStates, setCheckedStates] = useState({});
  const [selectAll, setSelectAll] = useState(true);

  useEffect(() => {
    const dateRange = handleCurrentMonthDate();
    setDateSelected(dateRange);
  }, []);

  // useEffect to initialize checkedStates on component mount
  useEffect(() => {
    if (envCOAData) {
      setCheckedStates(initializeCheckedStates(envCOAData, true));
    }
  }, [envCOAData]);

  const generateResult = (option, samples) => {
    const result = {};

    option.forEach((obj) => {
      const key = Object.keys(obj)[0]; // Get the option key, e.g., "All" or "Zone"
      const fields = obj[key]; // Get the array of fields or nested objects
      result[key] = {}; // Initialize the key in the result object

      Object.entries(samples).forEach(([sampleId, sample]) => {
        if (Array.isArray(fields)) {
          const concatenatedValue = fields
            .map((field) => sample[field]) // Get the value of each field
            .filter((value) => value) // Filter out empty or falsy values (e.g., null, undefined, or empty strings)
            .join(".");
          if (concatenatedValue) {
            if (!result[key][concatenatedValue]) {
              result[key][concatenatedValue] = [];
            }
            result[key][concatenatedValue].push(sampleId);
          }
        }

        fields.forEach((nestedObj) => {
          const nestedKey = Object.keys(nestedObj)[0]; // E.g., "section"
          const nestedFields = nestedObj[nestedKey]; // E.g., ["zone"]

          if (Array.isArray(nestedFields)) {
            const sectionValue = sample[nestedKey]; // Get section value from sample
            if (sectionValue !== undefined) { // Ensure sectionValue is valid
              const zoneValue = sample[nestedFields[0]]; // Get zone value from sample
              if (zoneValue !== undefined) { // Ensure zoneValue is valid
                result[key][sectionValue] = result[key][sectionValue] || {};
                result[key][sectionValue][zoneValue] = result[key][sectionValue][zoneValue] || [];
                result[key][sectionValue][zoneValue].push(sampleId);
              }
            }
          }
        });
      });
    });

    return result;
  };

  const getEnvCOAData = useCallback(async (payload) => {
    setLoading(true);
    const params = {
      date_from: payload.dateFrom,
      date_to: payload.dateTo,
      search: payload.search || "",
    };
    const response = await exportCOAData(params, { signal: controller?.signal });
    const { data, message, success } = response;

    if (success) {
      const { result, dates } = data;
      const allSample = Object.fromEntries(
        result.samples.map((item) => [uuid(), item]),
      );
      const finalResult = generateResult(result.options, allSample);
      const Data = {
        ...finalResult,
        samples: allSample,
      };
      setFilteredSamples(allSample);
      setEnvCOAData(Data);
      setCoaDates(dates);
      setSelectAll(true);
    } else if (message !== "Request canceled") {
      toast.error(message);
    }
    setLoading(false);
  }, [controller]);

  useEffect(() => {
    if (dateSelected) {
      const dateRange = dateSelected && convertDateRange(dateSelected);
      const payload = {
        dateFrom: dateRange.dateFrom,
        dateTo: dateRange.dateTo,
        search: search || "",
      };

      if (dateRange.dateFrom) {
        getEnvCOAData(payload);
      }
    }
  }, [dateSelected, search, getEnvCOAData]);

  const handleToggleAllFilterDrawer = () => {
    setShowAllFilterDrawer(!showAllFilterDrawer);
  };

  return (
    <Modal
      open
      closable={false}
      centered
      footer={null}
      onCancel={() => handleCloseModal()}
      className="ReportPreviewModal"
      width="750px"
      maskClosable={false} // Prevent modal close on outside click
    >
      <div className="ExportCOAModal__Container">
        <span>Export COA</span>
        <CloseOutlined
          className="ExportCOAModal__Container__CloseIcon"
          aria-label="Close"
          onClick={() => handleCloseModal()}
        />
      </div>
      <ExportCOAModalHeader handleToggleAllFilterDrawer={handleToggleAllFilterDrawer} setDateSelected={setDateSelected} dateSelected={dateSelected} filteredSamples={filteredSamples} dates={coaDates} />

      <div className="ExportCOADataPreview__Container">
        {showAllFilterDrawer
        && <ExportCOAAllFilter handleToggleAllFilterDrawer={handleToggleAllFilterDrawer} envCOAData={envCOAData} setSearch={setSearch} setFilteredSamples={setFilteredSamples} filteredSamples={filteredSamples} loading={loading} setController={setController} controller={controller} setCheckedStates={setCheckedStates} checkedStates={checkedStates} setSelectAll={setSelectAll} selectAll={selectAll} search={search} /> }

        {envCOAData && <ExportCOADataPreview filteredSamples={filteredSamples} loading={loading} />}
      </div>
    </Modal>
  );
}
